/**
 * Guided Tour Context
 *
 * Provides interactive step-by-step overlays for non-technical users.
 * Implements RFP requirements for:
 * - Step-by-step overlays
 * - Progress indicators
 * - Save-and-resume capability
 * - Role-based tour content
 */

import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect,
  ReactNode,
} from 'react';
import { CBORoleType } from '@/types/cboRoles';

// ============================================================================
// TYPES
// ============================================================================

export interface TourStep {
  id: string;
  target: string; // CSS selector for the target element
  title: string;
  titleEs: string;
  content: string;
  contentEs: string;
  placement?: 'top' | 'bottom' | 'left' | 'right' | 'center';
  spotlightPadding?: number;
  disableBeacon?: boolean;
  disableOverlay?: boolean;
  disableScrolling?: boolean;
  hideCloseButton?: boolean;
  showSkipButton?: boolean;
  showProgress?: boolean;
  requiredAction?: 'click' | 'input' | 'select' | 'none';
  waitForElement?: boolean;
  delay?: number;
  beforeStep?: () => void | Promise<void>;
  afterStep?: () => void | Promise<void>;
  validation?: () => boolean;
}

export interface Tour {
  id: string;
  name: string;
  nameEs: string;
  description: string;
  descriptionEs: string;
  roles: CBORoleType[];
  steps: TourStep[];
  category: 'onboarding' | 'feature' | 'workflow' | 'help';
  estimatedMinutes: number;
  requiredTour?: string; // ID of tour that must be completed first
}

export interface TourProgress {
  tourId: string;
  currentStepIndex: number;
  completedSteps: string[];
  startedAt: string;
  lastActiveAt: string;
  completed: boolean;
  completedAt?: string;
}

interface GuidedTourContextValue {
  // Current tour state
  activeTour: Tour | null;
  currentStep: TourStep | null;
  currentStepIndex: number;
  isRunning: boolean;
  isPaused: boolean;

  // Tour progress
  tourProgress: Record<string, TourProgress>;
  getProgress: (tourId: string) => TourProgress | null;
  hasCompletedTour: (tourId: string) => boolean;
  getCompletedTours: () => string[];

  // Tour control
  startTour: (tourId: string, startFromStep?: number) => void;
  pauseTour: () => void;
  resumeTour: () => void;
  stopTour: () => void;
  nextStep: () => void;
  prevStep: () => void;
  goToStep: (stepIndex: number) => void;
  skipTour: () => void;

  // Tour management
  availableTours: Tour[];
  getToursByCategory: (category: Tour['category']) => Tour[];
  getToursByRole: (role: CBORoleType) => Tour[];
  getRecommendedTours: (role: CBORoleType) => Tour[];

  // Help system
  showHelp: (contextKey: string) => void;
  hideHelp: () => void;
  activeHelpContext: string | null;
}

// ============================================================================
// STORAGE KEY
// ============================================================================

const STORAGE_KEY = 'weavein_tour_progress';

// ============================================================================
// PREDEFINED TOURS
// ============================================================================

const TOURS: Tour[] = [
  // Promotora Onboarding Tour
  {
    id: 'promotora-onboarding',
    name: 'Welcome to WeaveIn',
    nameEs: 'Bienvenido a WeaveIn',
    description: 'Learn the basics of navigating the platform',
    descriptionEs: 'Aprende los conceptos basicos de navegacion en la plataforma',
    roles: ['promotora', 'care_coordinator', 'staff'],
    category: 'onboarding',
    estimatedMinutes: 5,
    steps: [
      {
        id: 'welcome',
        target: 'body',
        title: 'Welcome!',
        titleEs: 'Bienvenido!',
        content: 'Let me show you around WeaveIn. This tour will help you get started with the most important features.',
        contentEs: 'Permiteme mostrarte WeaveIn. Este recorrido te ayudara a comenzar con las funciones mas importantes.',
        placement: 'center',
        disableOverlay: true,
        showProgress: true,
      },
      {
        id: 'navigation',
        target: '[data-tour="main-nav"]',
        title: 'Navigation Menu',
        titleEs: 'Menu de Navegacion',
        content: 'This is your main menu. Click on any item to go to that section.',
        contentEs: 'Este es tu menu principal. Haz clic en cualquier elemento para ir a esa seccion.',
        placement: 'right',
        showProgress: true,
      },
      {
        id: 'tasks',
        target: '[data-tour="tasks-section"]',
        title: 'Your Tasks',
        titleEs: 'Tus Tareas',
        content: 'Here you\'ll find your daily tasks. Complete them to help community members.',
        contentEs: 'Aqui encontraras tus tareas diarias. Completalas para ayudar a los miembros de la comunidad.',
        placement: 'bottom',
        showProgress: true,
      },
      {
        id: 'members',
        target: '[data-tour="members-link"]',
        title: 'Members',
        titleEs: 'Miembros',
        content: 'Click here to see all the community members you work with.',
        contentEs: 'Haz clic aqui para ver todos los miembros de la comunidad con los que trabajas.',
        placement: 'right',
        showProgress: true,
      },
      {
        id: 'quick-actions',
        target: '[data-tour="quick-actions"]',
        title: 'Quick Actions',
        titleEs: 'Acciones Rapidas',
        content: 'Use these buttons to quickly add members, log calls, or start enrollments.',
        contentEs: 'Usa estos botones para agregar miembros, registrar llamadas o iniciar inscripciones rapidamente.',
        placement: 'left',
        showProgress: true,
      },
      {
        id: 'help',
        target: '[data-tour="help-button"]',
        title: 'Need Help?',
        titleEs: 'Necesitas Ayuda?',
        content: 'Click this button anytime to get help or restart this tour.',
        contentEs: 'Haz clic en este boton en cualquier momento para obtener ayuda o reiniciar este recorrido.',
        placement: 'bottom',
        showProgress: true,
      },
      {
        id: 'complete',
        target: 'body',
        title: 'You\'re Ready!',
        titleEs: 'Estas Listo!',
        content: 'Great job! You now know the basics. Start by checking your tasks for today.',
        contentEs: 'Excelente! Ahora conoces lo basico. Comienza revisando tus tareas para hoy.',
        placement: 'center',
        disableOverlay: true,
        showProgress: true,
      },
    ],
  },

  // Enrollment Workflow Tour
  {
    id: 'enrollment-workflow',
    name: 'How to Complete an Enrollment',
    nameEs: 'Como Completar una Inscripcion',
    description: 'Step-by-step guide for enrolling community members',
    descriptionEs: 'Guia paso a paso para inscribir miembros de la comunidad',
    roles: ['promotora', 'care_coordinator', 'staff', 'supervisor'],
    category: 'workflow',
    estimatedMinutes: 8,
    requiredTour: 'promotora-onboarding',
    steps: [
      {
        id: 'start-enrollment',
        target: '[data-tour="start-enrollment"]',
        title: 'Start New Enrollment',
        titleEs: 'Iniciar Nueva Inscripcion',
        content: 'Click here to begin enrolling a new community member.',
        contentEs: 'Haz clic aqui para comenzar a inscribir a un nuevo miembro de la comunidad.',
        placement: 'bottom',
        showProgress: true,
      },
      {
        id: 'member-info',
        target: '[data-tour="member-info-form"]',
        title: 'Member Information',
        titleEs: 'Informacion del Miembro',
        content: 'Fill in the member\'s basic information. Fields marked with * are required.',
        contentEs: 'Completa la informacion basica del miembro. Los campos marcados con * son obligatorios.',
        placement: 'right',
        showProgress: true,
        waitForElement: true,
      },
      {
        id: 'language-preference',
        target: '[data-tour="language-select"]',
        title: 'Language Preference',
        titleEs: 'Preferencia de Idioma',
        content: 'Select the member\'s preferred language for communications.',
        contentEs: 'Selecciona el idioma preferido del miembro para las comunicaciones.',
        placement: 'bottom',
        showProgress: true,
      },
      {
        id: 'contact-info',
        target: '[data-tour="contact-section"]',
        title: 'Contact Information',
        titleEs: 'Informacion de Contacto',
        content: 'Add phone number and email. Choose how they prefer to be contacted.',
        contentEs: 'Agrega numero de telefono y correo electronico. Elige como prefieren ser contactados.',
        placement: 'right',
        showProgress: true,
      },
      {
        id: 'program-selection',
        target: '[data-tour="program-select"]',
        title: 'Select Program',
        titleEs: 'Seleccionar Programa',
        content: 'Choose which program the member is enrolling in.',
        contentEs: 'Elige en que programa se esta inscribiendo el miembro.',
        placement: 'bottom',
        showProgress: true,
      },
      {
        id: 'save-draft',
        target: '[data-tour="save-draft-btn"]',
        title: 'Save Your Progress',
        titleEs: 'Guarda tu Progreso',
        content: 'You can save your work and continue later by clicking "Save Draft".',
        contentEs: 'Puedes guardar tu trabajo y continuar despues haciendo clic en "Guardar Borrador".',
        placement: 'top',
        showProgress: true,
      },
      {
        id: 'submit',
        target: '[data-tour="submit-btn"]',
        title: 'Submit for Review',
        titleEs: 'Enviar para Revision',
        content: 'When everything is complete, click "Submit" to send for supervisor approval.',
        contentEs: 'Cuando todo este completo, haz clic en "Enviar" para enviarlo a aprobacion del supervisor.',
        placement: 'top',
        showProgress: true,
      },
    ],
  },

  // Supervisor Tour
  {
    id: 'supervisor-onboarding',
    name: 'Supervisor Dashboard Guide',
    nameEs: 'Guia del Panel de Supervisor',
    description: 'Learn how to manage your team and review cases',
    descriptionEs: 'Aprende a gestionar tu equipo y revisar casos',
    roles: ['supervisor', 'program_manager', 'org_admin'],
    category: 'onboarding',
    estimatedMinutes: 6,
    steps: [
      {
        id: 'welcome-supervisor',
        target: 'body',
        title: 'Welcome, Supervisor!',
        titleEs: 'Bienvenido, Supervisor!',
        content: 'This tour will show you how to manage your team and review their work.',
        contentEs: 'Este recorrido te mostrara como gestionar tu equipo y revisar su trabajo.',
        placement: 'center',
        showProgress: true,
      },
      {
        id: 'team-overview',
        target: '[data-tour="team-section"]',
        title: 'Team Overview',
        titleEs: 'Vista General del Equipo',
        content: 'See your team\'s status, task completion, and compliance scores at a glance.',
        contentEs: 'Ve el estado de tu equipo, tareas completadas y puntuaciones de cumplimiento de un vistazo.',
        placement: 'bottom',
        showProgress: true,
      },
      {
        id: 'review-queue',
        target: '[data-tour="review-queue"]',
        title: 'Review Queue',
        titleEs: 'Cola de Revision',
        content: 'Cases submitted by your team appear here for your review and approval.',
        contentEs: 'Los casos enviados por tu equipo aparecen aqui para tu revision y aprobacion.',
        placement: 'bottom',
        showProgress: true,
      },
      {
        id: 'compliance-metrics',
        target: '[data-tour="compliance-metrics"]',
        title: 'Compliance Metrics',
        titleEs: 'Metricas de Cumplimiento',
        content: 'Track key compliance indicators to ensure quality standards are met.',
        contentEs: 'Rastrea indicadores clave de cumplimiento para asegurar que se cumplan los estandares de calidad.',
        placement: 'left',
        showProgress: true,
      },
      {
        id: 'alerts',
        target: '[data-tour="alerts-section"]',
        title: 'Alerts & Notifications',
        titleEs: 'Alertas y Notificaciones',
        content: 'Important items requiring your attention appear here.',
        contentEs: 'Los elementos importantes que requieren tu atencion aparecen aqui.',
        placement: 'left',
        showProgress: true,
      },
    ],
  },

  // Care Plan Tour
  {
    id: 'care-plan-creation',
    name: 'Creating a Care Plan',
    nameEs: 'Crear un Plan de Cuidado',
    description: 'How to create and manage member care plans',
    descriptionEs: 'Como crear y gestionar planes de cuidado de miembros',
    roles: ['promotora', 'care_coordinator', 'supervisor'],
    category: 'workflow',
    estimatedMinutes: 10,
    steps: [
      {
        id: 'care-plan-intro',
        target: 'body',
        title: 'Care Plan Overview',
        titleEs: 'Vision General del Plan de Cuidado',
        content: 'A care plan documents the member\'s goals and the steps to achieve them.',
        contentEs: 'Un plan de cuidado documenta las metas del miembro y los pasos para alcanzarlas.',
        placement: 'center',
        showProgress: true,
      },
      {
        id: 'select-member',
        target: '[data-tour="member-search"]',
        title: 'Select Member',
        titleEs: 'Seleccionar Miembro',
        content: 'Search for and select the member you\'re creating a care plan for.',
        contentEs: 'Busca y selecciona el miembro para quien estas creando un plan de cuidado.',
        placement: 'bottom',
        showProgress: true,
      },
      {
        id: 'sdoh-assessment',
        target: '[data-tour="sdoh-section"]',
        title: 'SDOH Assessment',
        titleEs: 'Evaluacion SDOH',
        content: 'Review or update the member\'s social determinants of health information.',
        contentEs: 'Revisa o actualiza la informacion de determinantes sociales de salud del miembro.',
        placement: 'right',
        showProgress: true,
      },
      {
        id: 'add-goals',
        target: '[data-tour="add-goal-btn"]',
        title: 'Add Goals',
        titleEs: 'Agregar Metas',
        content: 'Click here to add health and wellness goals for the member.',
        contentEs: 'Haz clic aqui para agregar metas de salud y bienestar para el miembro.',
        placement: 'bottom',
        showProgress: true,
      },
      {
        id: 'action-steps',
        target: '[data-tour="action-steps"]',
        title: 'Action Steps',
        titleEs: 'Pasos de Accion',
        content: 'Define specific action steps to help the member achieve each goal.',
        contentEs: 'Define pasos de accion especificos para ayudar al miembro a alcanzar cada meta.',
        placement: 'right',
        showProgress: true,
      },
      {
        id: 'schedule-followup',
        target: '[data-tour="followup-schedule"]',
        title: 'Schedule Follow-ups',
        titleEs: 'Programar Seguimientos',
        content: 'Set up regular follow-up appointments to track progress.',
        contentEs: 'Configura citas de seguimiento regulares para rastrear el progreso.',
        placement: 'bottom',
        showProgress: true,
      },
    ],
  },

  // Feature: Accessibility
  {
    id: 'accessibility-features',
    name: 'Accessibility Settings',
    nameEs: 'Configuracion de Accesibilidad',
    description: 'Customize the display for your needs',
    descriptionEs: 'Personaliza la visualizacion segun tus necesidades',
    roles: ['promotora', 'care_coordinator', 'staff', 'supervisor', 'program_manager', 'org_admin', 'super_admin', 'viewer'],
    category: 'feature',
    estimatedMinutes: 3,
    steps: [
      {
        id: 'accessibility-button',
        target: '[data-tour="accessibility-btn"]',
        title: 'Accessibility Settings',
        titleEs: 'Configuracion de Accesibilidad',
        content: 'Click here to customize how the application looks and feels.',
        contentEs: 'Haz clic aqui para personalizar como se ve y funciona la aplicacion.',
        placement: 'bottom',
        showProgress: true,
      },
      {
        id: 'text-size',
        target: '[data-tour="text-size-setting"]',
        title: 'Text Size',
        titleEs: 'Tamano de Texto',
        content: 'Increase the text size if you find it difficult to read.',
        contentEs: 'Aumenta el tamano del texto si te resulta dificil leer.',
        placement: 'right',
        showProgress: true,
        waitForElement: true,
      },
      {
        id: 'high-contrast',
        target: '[data-tour="contrast-setting"]',
        title: 'High Contrast',
        titleEs: 'Alto Contraste',
        content: 'Turn on high contrast mode for better visibility.',
        contentEs: 'Activa el modo de alto contraste para mejor visibilidad.',
        placement: 'right',
        showProgress: true,
      },
    ],
  },
];

// ============================================================================
// CONTEXT
// ============================================================================

const GuidedTourContext = createContext<GuidedTourContextValue | undefined>(undefined);

// ============================================================================
// PROVIDER
// ============================================================================

interface GuidedTourProviderProps {
  children: ReactNode;
}

export function GuidedTourProvider({ children }: GuidedTourProviderProps) {
  const [activeTour, setActiveTour] = useState<Tour | null>(null);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [activeHelpContext, setActiveHelpContext] = useState<string | null>(null);
  const [tourProgress, setTourProgress] = useState<Record<string, TourProgress>>(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored) {
        try {
          return JSON.parse(stored);
        } catch {
          return {};
        }
      }
    }
    return {};
  });

  // Persist progress
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(tourProgress));
    }
  }, [tourProgress]);

  // Current step
  const currentStep = activeTour?.steps[currentStepIndex] ?? null;

  /**
   * Get progress for a specific tour
   */
  const getProgress = useCallback((tourId: string): TourProgress | null => {
    return tourProgress[tourId] ?? null;
  }, [tourProgress]);

  /**
   * Check if a tour has been completed
   */
  const hasCompletedTour = useCallback((tourId: string): boolean => {
    return tourProgress[tourId]?.completed ?? false;
  }, [tourProgress]);

  /**
   * Get list of completed tour IDs
   */
  const getCompletedTours = useCallback((): string[] => {
    return Object.entries(tourProgress)
      .filter(([_, progress]) => progress.completed)
      .map(([id]) => id);
  }, [tourProgress]);

  /**
   * Start a tour
   */
  const startTour = useCallback((tourId: string, startFromStep: number = 0) => {
    const tour = TOURS.find(t => t.id === tourId);
    if (!tour) {
      console.error(`Tour not found: ${tourId}`);
      return;
    }

    // Check if required tour is completed
    if (tour.requiredTour && !hasCompletedTour(tour.requiredTour)) {
      console.warn(`Required tour not completed: ${tour.requiredTour}`);
      // Could show a message to the user here
    }

    setActiveTour(tour);
    setCurrentStepIndex(startFromStep);
    setIsRunning(true);
    setIsPaused(false);

    // Update progress
    const now = new Date().toISOString();
    setTourProgress(prev => ({
      ...prev,
      [tourId]: {
        tourId,
        currentStepIndex: startFromStep,
        completedSteps: prev[tourId]?.completedSteps ?? [],
        startedAt: prev[tourId]?.startedAt ?? now,
        lastActiveAt: now,
        completed: false,
      },
    }));
  }, [hasCompletedTour]);

  /**
   * Pause the current tour
   */
  const pauseTour = useCallback(() => {
    if (activeTour) {
      setIsPaused(true);
      setTourProgress(prev => ({
        ...prev,
        [activeTour.id]: {
          ...prev[activeTour.id],
          lastActiveAt: new Date().toISOString(),
        },
      }));
    }
  }, [activeTour]);

  /**
   * Resume a paused tour
   */
  const resumeTour = useCallback(() => {
    setIsPaused(false);
  }, []);

  /**
   * Stop the current tour
   */
  const stopTour = useCallback(() => {
    if (activeTour) {
      setTourProgress(prev => ({
        ...prev,
        [activeTour.id]: {
          ...prev[activeTour.id],
          lastActiveAt: new Date().toISOString(),
        },
      }));
    }
    setActiveTour(null);
    setCurrentStepIndex(0);
    setIsRunning(false);
    setIsPaused(false);
  }, [activeTour]);

  /**
   * Go to next step
   */
  const nextStep = useCallback(async () => {
    if (!activeTour) return;

    const step = activeTour.steps[currentStepIndex];

    // Run validation if defined
    if (step?.validation && !step.validation()) {
      return; // Don't proceed if validation fails
    }

    // Run afterStep callback if defined
    if (step?.afterStep) {
      await step.afterStep();
    }

    // Mark current step as completed
    const stepId = step?.id;
    if (stepId) {
      setTourProgress(prev => ({
        ...prev,
        [activeTour.id]: {
          ...prev[activeTour.id],
          completedSteps: [...new Set([...(prev[activeTour.id]?.completedSteps ?? []), stepId])],
          lastActiveAt: new Date().toISOString(),
        },
      }));
    }

    // Move to next step or complete tour
    if (currentStepIndex < activeTour.steps.length - 1) {
      const nextIndex = currentStepIndex + 1;
      setCurrentStepIndex(nextIndex);

      // Run beforeStep callback for next step
      const nextStepDef = activeTour.steps[nextIndex];
      if (nextStepDef?.beforeStep) {
        await nextStepDef.beforeStep();
      }

      setTourProgress(prev => ({
        ...prev,
        [activeTour.id]: {
          ...prev[activeTour.id],
          currentStepIndex: nextIndex,
        },
      }));
    } else {
      // Tour completed
      setTourProgress(prev => ({
        ...prev,
        [activeTour.id]: {
          ...prev[activeTour.id],
          completed: true,
          completedAt: new Date().toISOString(),
        },
      }));
      stopTour();
    }
  }, [activeTour, currentStepIndex, stopTour]);

  /**
   * Go to previous step
   */
  const prevStep = useCallback(() => {
    if (currentStepIndex > 0) {
      setCurrentStepIndex(currentStepIndex - 1);
    }
  }, [currentStepIndex]);

  /**
   * Go to a specific step
   */
  const goToStep = useCallback((stepIndex: number) => {
    if (activeTour && stepIndex >= 0 && stepIndex < activeTour.steps.length) {
      setCurrentStepIndex(stepIndex);
    }
  }, [activeTour]);

  /**
   * Skip the current tour
   */
  const skipTour = useCallback(() => {
    if (activeTour) {
      setTourProgress(prev => ({
        ...prev,
        [activeTour.id]: {
          ...prev[activeTour.id],
          lastActiveAt: new Date().toISOString(),
        },
      }));
    }
    stopTour();
  }, [activeTour, stopTour]);

  /**
   * Get tours by category
   */
  const getToursByCategory = useCallback((category: Tour['category']): Tour[] => {
    return TOURS.filter(t => t.category === category);
  }, []);

  /**
   * Get tours by role
   */
  const getToursByRole = useCallback((role: CBORoleType): Tour[] => {
    return TOURS.filter(t => t.roles.includes(role));
  }, []);

  /**
   * Get recommended tours for a role
   */
  const getRecommendedTours = useCallback((role: CBORoleType): Tour[] => {
    const roleTours = getToursByRole(role);
    return roleTours.filter(tour => {
      // Recommend if not completed and required tour is done (or no requirement)
      if (hasCompletedTour(tour.id)) return false;
      if (tour.requiredTour && !hasCompletedTour(tour.requiredTour)) return false;
      return true;
    });
  }, [getToursByRole, hasCompletedTour]);

  /**
   * Show contextual help
   */
  const showHelp = useCallback((contextKey: string) => {
    setActiveHelpContext(contextKey);
  }, []);

  /**
   * Hide contextual help
   */
  const hideHelp = useCallback(() => {
    setActiveHelpContext(null);
  }, []);

  const contextValue: GuidedTourContextValue = {
    activeTour,
    currentStep,
    currentStepIndex,
    isRunning,
    isPaused,
    tourProgress,
    getProgress,
    hasCompletedTour,
    getCompletedTours,
    startTour,
    pauseTour,
    resumeTour,
    stopTour,
    nextStep,
    prevStep,
    goToStep,
    skipTour,
    availableTours: TOURS,
    getToursByCategory,
    getToursByRole,
    getRecommendedTours,
    showHelp,
    hideHelp,
    activeHelpContext,
  };

  return (
    <GuidedTourContext.Provider value={contextValue}>
      {children}
    </GuidedTourContext.Provider>
  );
}

// ============================================================================
// HOOKS
// ============================================================================

export function useGuidedTour(): GuidedTourContextValue {
  const context = useContext(GuidedTourContext);
  if (context === undefined) {
    throw new Error('useGuidedTour must be used within a GuidedTourProvider');
  }
  return context;
}

export function useTourStep(tourId: string, stepId: string): {
  isActive: boolean;
  isCompleted: boolean;
} {
  const { activeTour, currentStep, getProgress } = useGuidedTour();
  const progress = getProgress(tourId);

  return {
    isActive: activeTour?.id === tourId && currentStep?.id === stepId,
    isCompleted: progress?.completedSteps.includes(stepId) ?? false,
  };
}

export default GuidedTourContext;
